import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getChallengeLeaderboard, verifyChallengeScore } from '../api';
import homeCache from '../services/homeCache';

const ChallengeLeaderboard = ({ routineId, challengeType, challengeEndDate }) => {
    const cacheKey = routineId ? `challenge_lb_${routineId}` : null;
    const cachedData = cacheKey ? homeCache.get(cacheKey) : null;
    const [leaderboard, setLeaderboard] = useState(cachedData?.top_10 ?? (Array.isArray(cachedData) ? cachedData : []));
    const [currentUserRank, setCurrentUserRank] = useState(cachedData?.current_user_rank ?? null);
    const [loading, setLoading] = useState(!cachedData);
    const [error, setError] = useState(null);

    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');
    const [lockoutTime, setLockoutTime] = useState(0);

    const fetchLeaderboard = useCallback(async (signal) => {
        if (!cacheKey) return;
        if (!homeCache.has(cacheKey)) {
            setLoading(true);
        }
        try {
            const response = await getChallengeLeaderboard(routineId, { signal });
            if (signal?.aborted) return;
            let data = response.data;
            if (data && data.top_10) {
                setLeaderboard(data.top_10);
                setCurrentUserRank(data.current_user_rank);
                homeCache.set(cacheKey, data);
            } else if (Array.isArray(data)) {
                setLeaderboard(data);
                setCurrentUserRank(null);
                homeCache.set(cacheKey, data);
            } else {
                setLeaderboard([]);
            }
            setError(null);
        } catch (err) {
            if (signal?.aborted) return;
            console.error("Failed to fetch leaderboard", err);
            setError('Failed to load leaderboard data.');
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [routineId, cacheKey]);

    useEffect(() => {
        const controller = new AbortController();
        if (routineId) {
            fetchLeaderboard(controller.signal);
        }
        return () => {
            controller.abort();
        };
    }, [fetchLeaderboard, routineId]);

    useEffect(() => {
        const storedLockout = localStorage.getItem('pin_lockout_time');
        if (storedLockout) {
            const time = parseInt(storedLockout, 10);
            if (Date.now() < time) {
                setLockoutTime(time);
            } else {
                localStorage.removeItem('pin_lockout_time');
                localStorage.removeItem('pin_attempts');
            }
        }
    }, [verifyModalOpen]);

    useEffect(() => {
        let interval;
        if (lockoutTime > Date.now()) {
            interval = setInterval(() => {
                if (Date.now() >= lockoutTime) {
                    setLockoutTime(0);
                    localStorage.removeItem('pin_lockout_time');
                    localStorage.removeItem('pin_attempts');
                    setPinError('');
                } else {
                    setLockoutTime(lockoutTime); 
                }
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [lockoutTime]);

    const handleVerifyClick = (recordId) => {
        if (lockoutTime > Date.now()) {
            setPinError('Locked out for 5 minutes.');
        } else {
            setPinError('');
        }
        setSelectedRecordId(recordId);
        setPinInput('');
        setVerifyModalOpen(true);
    };

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        if (Date.now() < lockoutTime) return;

        if (!/^[a-zA-Z0-9]{4,6}$/.test(pinInput)) {
             setPinError('PIN must be 4 to 6 alphanumeric characters.');
             return;
        }

        try {
            await verifyChallengeScore(selectedRecordId, pinInput);
            setVerifyModalOpen(false);
            localStorage.removeItem('pin_attempts');
            fetchLeaderboard();
        } catch (error) {
            let attempts = parseInt(localStorage.getItem('pin_attempts') || '0', 10) + 1;
            localStorage.setItem('pin_attempts', attempts);
            if (attempts >= 3) {
                const unlockTime = Date.now() + 5 * 60 * 1000;
                localStorage.setItem('pin_lockout_time', unlockTime);
                setLockoutTime(unlockTime);
                setPinError('Too many failed attempts. Locked out for 5 minutes.');
            } else {
                setPinError(`Invalid PIN. ${3 - attempts} attempt(s) remaining.`);
            }
        }
    };
    const calculateTimeRemaining = useCallback((endDateStr) => {
        if (!endDateStr) return null;
        const end = new Date(endDateStr);
        const now = new Date();
        const diffMs = end - now;

        if (diffMs <= 0) {
            return `Ended on ${end.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric' })}`;
        }

        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (diffDays > 0) {
            return `Ends in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
        }
        return `Ends in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    }, []);

    const timeStatus = useMemo(() => calculateTimeRemaining(challengeEndDate), [calculateTimeRemaining, challengeEndDate]);

    if (loading) {
        return (
            <div className="mt-6 bg-gray-900/40 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-10 bg-gray-800 rounded"></div>
                    <div className="h-10 bg-gray-800 rounded"></div>
                    <div className="h-10 bg-gray-800 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-6 bg-red-900/20 text-red-400 p-4 rounded-xl border border-red-900/50 text-center">
                <p className="text-sm">{error}</p>
            </div>
        );
    }

    if (!leaderboard || leaderboard.length === 0) {
        return (
            <div className="mt-6 bg-gray-900/40 rounded-xl p-6 text-center border border-gray-700/50">
                <svg className="w-12 h-12 mx-auto text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <h4 className="text-gray-300 font-medium tracking-wide">No Entries Yet</h4>
                <p className="text-sm text-gray-500 mt-1">Be the first to log a verified score!</p>
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="flex justify-between items-center border-b border-gray-700 pb-2 mb-4">
                <h3 className="text-sm font-semibold text-teal-400 uppercase tracking-wider flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Verified Leaderboard
                </h3>
                {timeStatus && (
                    <span className="text-xs font-semibold px-2 py-1 bg-gray-800 text-gray-300 rounded-md border border-gray-600/50">
                        {timeStatus}
                    </span>
                )}
            </div>
            <div className="bg-gray-900/60 rounded-xl border border-gray-700/50 overflow-hidden shadow-inner">
                {leaderboard.map((entry, index) => {
                    const isPending = entry.status === 'PENDING';
                    const isCurrentUser = currentUserRank?.record?.id === entry.id;
                    return (
                    <div 
                        key={entry.id} 
                        className={`flex items-center justify-between p-4 ${index !== leaderboard.length - 1 ? 'border-b border-gray-800' : ''} ${index === 0 ? 'bg-gradient-to-r from-yellow-500/10 to-transparent' : ''} ${isPending ? 'opacity-75' : ''} ${isPending && isCurrentUser ? 'cursor-pointer hover:bg-gray-800' : ''}`}
                        onClick={() => isPending && isCurrentUser && handleVerifyClick(entry.id)}
                    >
                        <div className="flex items-center gap-4">
                            <span className={`font-mono font-bold w-6 text-center ${index === 0 ? 'text-yellow-400 text-lg' : index === 1 ? 'text-gray-400 text-lg' : index === 2 ? 'text-orange-400 text-lg' : 'text-gray-500'}`}>
                                #{index + 1}
                            </span>
                            <div>
                                <div className="font-bold text-gray-200">{entry.player_name}</div>
                                <div className="text-xs text-gray-500 flex flex-col gap-1 mt-1">
                                    {isPending ? (
                                        <div className="flex items-center gap-1 text-orange-400">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Pending Verification
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1">
                                            <svg className="w-3 h-3 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Verified on {entry.completed_at ? new Date(entry.completed_at).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown'}
                                        </div>
                                    )}
                                    {entry.verified_by_user_name && !isPending && (
                                        <div className="flex items-center gap-1 text-teal-600/80">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            by {entry.verified_by_user_name}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="text-xl font-mono font-bold text-teal-300">
                            {challengeType === 'FOR_TIME' 
                                ? `${Math.floor(entry.score_numeric / 60)}:${String(Math.round(entry.score_numeric % 60)).padStart(2, '0')}`
                                : `${entry.score_numeric} Reps`}
                        </div>
                    </div>
                )})}
            </div>

            {currentUserRank && currentUserRank.rank > 10 && (
                <div 
                    className={`mt-4 bg-gray-800/80 rounded-xl border-2 border-primary/40 overflow-hidden shadow-lg p-4 flex items-center justify-between transition-colors ${currentUserRank?.record?.status === 'PENDING' ? 'opacity-75 cursor-pointer hover:bg-gray-800' : 'hover:bg-gray-800'}`}
                    onClick={() => currentUserRank?.record?.status === 'PENDING' && handleVerifyClick(currentUserRank.record.id)}
                >
                    <div className="flex items-center gap-4">
                        <span className="font-mono font-bold w-6 text-center text-primary/80 text-lg">
                            #{currentUserRank.rank}
                        </span>
                        <div>
                            <div className="font-bold text-white flex items-center gap-2">
                                Your Rank
                                <span className="bg-primary/20 text-primary px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">You</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                                {currentUserRank?.record?.status === 'PENDING' ? (
                                    <span className="text-orange-400 font-semibold flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Pending Verification
                                    </span>
                                ) : (
                                    <span>Verified on {currentUserRank?.record?.completed_at ? new Date(currentUserRank.record.completed_at).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown'}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="text-xl font-mono font-bold text-primary">
                        {challengeType === 'FOR_TIME' 
                            ? `${Math.floor(currentUserRank.score_numeric / 60)}:${String(Math.round(currentUserRank.score_numeric % 60)).padStart(2, '0')}`
                            : `${currentUserRank.score_numeric} Reps`}
                    </div>
                </div>
            )}

            {verifyModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
                    <div className="bg-gray-900 rounded-2xl border border-gray-700/50 p-6 w-full max-w-md shadow-2xl relative">
                        <button 
                            onClick={() => setVerifyModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <h2 className="text-xl font-bold text-white mb-2">Verify Score</h2>
                        <p className="text-sm text-gray-400 mb-6">Enter the 4 to 6 character code of the witness to verify this score.</p>
                        
                        <form onSubmit={handlePinSubmit} className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    maxLength="6"
                                    value={pinInput}
                                    onChange={(e) => setPinInput(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
                                    disabled={lockoutTime > Date.now()}
                                    className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-teal-500 disabled:opacity-50"
                                    placeholder="----"
                                />
                            </div>
                            {pinError && <p className="text-red-400 text-sm text-center">{pinError}</p>}
                            <button
                                type="submit"
                                disabled={lockoutTime > Date.now() || pinInput.length < 4}
                                className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {lockoutTime > Date.now() ? `Locked (${Math.ceil((lockoutTime - Date.now()) / 1000)}s)` : 'Verify'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(ChallengeLeaderboard);
